import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "mp-theme";
const SHADE_ROUTES = ["/colours", "/catalogue"];

const ThemeContext = createContext({ theme: "light", isShadeRoute: false, toggle: () => {} });

export const ThemeProvider = ({ children }) => {
  const location = useLocation();
  const [choice, setChoice] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      console.debug("Stored theme unreadable, falling back to route default:", e);
      return null;
    }
  });

  const isShadeRoute = SHADE_ROUTES.some((r) => location.pathname.startsWith(r));
  // Shade/swatch pages open in Showroom (dark) unless the visitor picked a theme
  const theme = choice || (isShadeRoute ? "dark" : "light");

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.classList.toggle("dark", theme === "dark");
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#0C0C0C" : "#101010");
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      isShadeRoute,
      toggle: () => {
        const next = theme === "dark" ? "light" : "dark";
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // storage blocked (private mode / disabled cookies) — theme still applies this session
          console.debug("Theme preference could not be saved to localStorage");
        }
        setChoice(next);
      },
    }),
    [theme, isShadeRoute]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
